import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './BrickBreaker.css';

const BrickBreaker = () => {
  const [paddle, setPaddle] = useState({ x: 150, width: 100 });
  const [ball, setBall] = useState({ x: 200, y: 200, dx: 4, dy: 4 });
  const [bricks, setBricks] = useState([]);
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);

  const gameAreaRef = useRef(null);
  const animationRef = useRef(null);

  const GAME_WIDTH = 400;
  const GAME_HEIGHT = 400;
  const PADDLE_HEIGHT = 10;
  const BALL_SIZE = 10;
  const BRICK_ROWS = 5;
  const BRICK_COLS = 8;

  const initializeBricks = () => {
    const newBricks = [];
    for (let row = 0; row < BRICK_ROWS; row++) {
      for (let col = 0; col < BRICK_COLS; col++) {
        newBricks.push({
          x: col * 47 + 10,
          y: row * 25 + 50,
          width: 45,
          height: 20,
          destroyed: false
        });
      }
    }
    return newBricks;
  };

  const startGame = () => {
    setGameActive(true);
    setGameOver(false);
    setGameWon(false);
    setScore(0);
    setPaddle({ x: 150, width: 100 });
    setBall({ x: 200, y: 200, dx: 4, dy: 4 });
    setBricks(initializeBricks());
  };

  const handleMouseMove = useCallback((e) => {
    if (!gameActive) return;
    
    const rect = gameAreaRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const mouseX = e.clientX - rect.left;
    const paddleX = Math.max(0, Math.min(mouseX - 50, GAME_WIDTH - 100)); // 50 = half paddle width
    
    setPaddle(prev => ({ ...prev, x: paddleX }));
  }, [gameActive]);

  const checkCollision = (rect1, rect2) => {
    return rect1.x < rect2.x + rect2.width &&
           rect1.x + rect1.width > rect2.x &&
           rect1.y < rect2.y + rect2.height &&
           rect1.y + rect1.height > rect2.y;
  };

  const updateGame = useCallback(() => {
    if (!gameActive || gameOver || gameWon) return;

    setBall(prevBall => {
      let newBall = { ...prevBall };
      
      // Calculate new position
      newBall.x += newBall.dx;
      newBall.y += newBall.dy;

      // Wall collisions
      if (newBall.x <= 0 || newBall.x >= GAME_WIDTH - BALL_SIZE) {
        newBall.dx = -newBall.dx;
        newBall.x = Math.max(0, Math.min(newBall.x, GAME_WIDTH - BALL_SIZE));
      }
      
      if (newBall.y <= 0) {
        newBall.dy = -newBall.dy;
        newBall.y = 0;
      }

      return newBall;
    });

    // Paddle collision - separate state update
    setBall(prevBall => {
      const paddleY = GAME_HEIGHT - 50;
      if (prevBall.dy > 0 && 
          prevBall.y + BALL_SIZE >= paddleY && 
          prevBall.y <= paddleY + PADDLE_HEIGHT &&
          prevBall.x + BALL_SIZE >= paddle.x && 
          prevBall.x <= paddle.x + paddle.width) {
        
        const newBall = { ...prevBall };
        newBall.dy = -Math.abs(newBall.dy);
        newBall.y = paddleY - BALL_SIZE;
        
        // Add angle based on where ball hits paddle
        const paddleCenter = paddle.x + paddle.width / 2;
        const hitPos = (newBall.x + BALL_SIZE / 2 - paddleCenter) / (paddle.width / 2);
        newBall.dx += hitPos * 2;
        
        // Limit speed
        const maxSpeed = 7;
        newBall.dx = Math.max(-maxSpeed, Math.min(maxSpeed, newBall.dx));
        newBall.dy = Math.max(-maxSpeed, Math.min(maxSpeed, newBall.dy));
        
        return newBall;
      }
      return prevBall;
    });

    // Game over check
    setBall(prevBall => {
      if (prevBall.y >= GAME_HEIGHT) {
        setGameActive(false);
        setGameOver(true);
      }
      return prevBall;
    });

    // Brick collisions
    setBricks(prevBricks => {
      const newBricks = [...prevBricks];
      let hitBrick = false;

      newBricks.forEach(brick => {
        if (!brick.destroyed) {
          // Get current ball position
          const ballRect = { 
            x: ball.x, 
            y: ball.y, 
            width: BALL_SIZE, 
            height: BALL_SIZE 
          };
          
          if (checkCollision(ballRect, brick)) {
            brick.destroyed = true;
            hitBrick = true;
            setScore(prev => prev + 10);
          }
        }
      });

      if (hitBrick) {
        setBall(prev => ({ ...prev, dy: -prev.dy }));
        
        // Check win condition
        if (newBricks.every(brick => brick.destroyed)) {
          setGameActive(false);
          setGameWon(true);
        }
      }

      return newBricks;
    });
  }, [gameActive, gameOver, gameWon, paddle, ball]);

  useEffect(() => {
    if (gameActive) {
      const gameLoop = () => {
        updateGame();
        animationRef.current = requestAnimationFrame(gameLoop);
      };
      animationRef.current = requestAnimationFrame(gameLoop);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [gameActive, updateGame]);

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">🧱 Brick Breaker</h1>
        <div className="game-instructions">
          Move your mouse to control the paddle. Break all the bricks to win!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">Score: {score}</div>
        
        <div className="game-play-area">
          <div 
            ref={gameAreaRef}
            className="brick-breaker-area"
            onMouseMove={handleMouseMove}
          >
            {/* Bricks */}
            {bricks.map((brick, index) => (
              !brick.destroyed && (
                <div
                  key={index}
                  className="brick"
                  style={{
                    left: brick.x,
                    top: brick.y,
                    width: brick.width,
                    height: brick.height
                  }}
                />
              )
            ))}
            
            {/* Ball */}
            <div
              className="ball"
              style={{
                left: ball.x,
                top: ball.y,
                width: BALL_SIZE,
                height: BALL_SIZE
              }}
            />
            
            {/* Paddle */}
            <div
              className="paddle"
              style={{
                left: paddle.x,
                width: paddle.width,
                bottom: 40
              }}
            />

            {/* Game status overlay */}
            {!gameActive && (
              <div className="game-status-overlay">
                {gameWon && <div className="status-message win">🎉 You Won!</div>}
                {gameOver && <div className="status-message lose">💥 Game Over!</div>}
                {!gameWon && !gameOver && (
                  <div className="status-message start">Click Start Game to begin!</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver || gameWon ? 'Play Again' : 'Start Game'}
          </button>
          <Link to="/" className="btn btn-secondary">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BrickBreaker;